import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Eye, FileSearch2, Image as ImageIcon, Upload, X } from 'lucide-react';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import ButtonComponent from 'src/components/shared/ButtonComponent';
import NavbarBreadcrumbV2 from 'src/components/shared/NavbarBreadcrumbV2';
import { Textarea } from 'src/components/ui/textarea';
import { AppAgentContext } from 'src/context/ContextProvider';
import { getAgentByIdServiceV2, getAllAgentsServiceV2 } from 'src/services/AgentService';
import { createFolderService, uploadFileCompressedService } from 'src/services/AssetService';
import { analyzeDocumentOrImageService } from 'src/services/DocumentOrImageV2Service';
import { SkillType } from 'src/types/AiAgentV2';
import { SelectComponent } from 'src/utils/UtilsComponent';
import { formatSizeUnits, uuidV4 } from '../../../utils';
import { Button } from '../../ui/button';
import { Card, CardContent, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import iziToast from 'izitoast';
import { IMAGES_V2_CREATE } from 'src/types/Permission';
import { DeniedComponent } from 'src/App';
import Permission from 'src/components/shared/Permission';

const classNameLabel = 'form-input-label';
const AnalizeImagesComponent = () => {
    const {
        state: {
            session: {
                user_id,
                ai_agent_config: {
                    folders: { analyze_images },
                },
            },
        },
    } = useContext(AppAgentContext);
    const [files, setFiles] = useState<File[]>([]);
    const [agentSelected, setAgentSelected] = useState('');
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t: common } = useTranslate('common');
    const { t: agent } = useTranslate('agent');

    const { t: images } = useTranslate('image');
    const navigate = useNavigate();
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const newFiles = Array.from(event.target.files).map((file) => file);
            setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        }
    };

    const { data: agents } = useQuery({
        queryKey: ['agents'],
        queryFn: async () =>
            await getAllAgentsServiceV2().then((res) =>
                res.items.map((item) => ({
                    label: item.name,
                    value: item.id ?? 1,
                }))
            ),
    });

    const { data: agentsWithSkills } = useQuery({
        queryKey: ['agentsWithSkills'],
        queryFn: async () =>
            await Promise.all(
                (agents || []).map(async (agent) => {
                    return (await getAgentByIdServiceV2(`${agent.value}`)).item;
                })
            ),
        enabled: (agents || []).length > 0,
    });

    const agentsFilter = useMemo(() => {
        if (agentsWithSkills) {
            const data = agentsWithSkills.filter((ag) =>
                ag.skills?.some((at) => at.skill_type === SkillType.SkillImageAnalysis)
            );
            if (data.length > 0) {
                return data.map((item) => ({
                    label: item.name,
                    value: item.id ?? 1,
                }));
            }
        }
        return [];
    }, [agentsWithSkills]);

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (event.dataTransfer.files) {
            const newFiles = Array.from(event.dataTransfer.files).map((file) => file);
            setFiles((prevFiles) => [...prevFiles, ...newFiles]);
        }
    };

    const analyzeImages = async () => {
        setLoading(true);
        // const filesCompressed = await Promise.all(files.map((file) => compressImageService(file)));
        // const filesCompressedBase64 = await Promise.all(
        //     filesCompressed.map(async (file) => {
        //         const image = await fileToBase64(file);
        //         return {
        //             name: file.name.replaceAll(' ', '_'),
        //             value: image,
        //         };
        //     }),
        // );

        const resp = await createFolderService(analyze_images, uuidV4());
        if (resp.success) {
            const rest = await uploadFileCompressedService(resp.folder.key, files);
            if (rest.success) {
                //TODO VALIDATE RESPONSE @DIEGO NORIEGA
                const res = await analyzeDocumentOrImageService({
                    agent_id: agentSelected,
                    user: user_id,
                    question,
                    test: false,
                    history: false,
                    key_filter: 'image_analysis',
                    session_id: new Date().getTime().toString(),
                    meta_data: {
                        folder: resp.folder.key,
                    },
                });

                if (res.success) {
                    iziToast.success({
                        message: images('analyzing_images'),
                        position: 'topCenter',
                    });
                    navigate('/images-v2');
                    setLoading(false);
                } else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        }
    };

    return (
        <Permission permission={IMAGES_V2_CREATE} denied={<DeniedComponent />}>
            <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 p-4 sm:p-8">
                <NavbarBreadcrumbV2
                    routes={[
                        {
                            label: images('image'),
                            route: '/images-v2',
                        },
                        {
                            label: images('analyze_images'),
                            route: '',
                        },
                    ]}
                />

                <h1 className="title">{images('analyze_images')}</h1>
                <Card className="w-full max-w-4xl  mx-auto bg-white bg-opacity-90 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden">
                    <CardContent className="p-6 grid grid-cols-1 gap-4">
                        <div>
                            <div className="mb-4">
                                <CardTitle className="text-lg font-semibold text-purple-700 my-4">
                                    {agent('agent')}
                                </CardTitle>
                                <SelectComponent
                                    value={agentSelected}
                                    onChange={(value) => {
                                        setAgentSelected(value);
                                    }}
                                    options={agentsFilter}
                                    classNameLabel={classNameLabel}
                                    required={true}
                                />
                            </div>

                            <div className="flex items-center space-x-4">
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <motion.div
                                                className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                                whileHover={{ rotate: 15 }}
                                                whileTap={{ scale: 0.95 }}
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={handleDrop}
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                <Upload className="h-6 w-6 text-white" />
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    multiple
                                                    onChange={handleFileUpload}
                                                    accept="image/*"
                                                    ref={fileInputRef}
                                                />
                                            </motion.div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{common('drag_and_drop')}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <div className="flex-grow">
                                    <CardTitle className="text-lg font-semibold text-purple-700 mb-2">
                                        {common('files')}
                                    </CardTitle>
                                    <ScrollArea className="h-32 w-full rounded-md border border-gray-200 p-2">
                                        {files.length === 0 ? (
                                            <p className="text-gray-500 text-sm">{agent('click_icon_upload_files')}</p>
                                        ) : (
                                            <ul className="space-y-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 items-start">
                                                {files.map((file, index) => (
                                                    <motion.li
                                                        key={index}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: 20 }}
                                                        transition={{ duration: 0.3 }}
                                                        className="flex flex-col mt-0 items-center justify-between bg-purple-50 rounded-lg p-12text-sm"
                                                    >
                                                        <div className="flex items-center space-x-2">
                                                            <ImageIcon className="h-4 w-4 text-purple-500" />
                                                            <span className="text-gray-700 truncate max-w-[150px]">
                                                                {file.name}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <span className="text-xs text-gray-500">
                                                                {formatSizeUnits(file.size)}
                                                            </span>
                                                            <Dialog>
                                                                <DialogTrigger asChild>
                                                                    <Button variant="ghost" size="sm" className="p-1">
                                                                        <Eye className="h-4 w-4 text-purple-500" />
                                                                    </Button>
                                                                </DialogTrigger>
                                                                <DialogContent className="sm:max-w-[425px]">
                                                                    <DialogHeader>
                                                                        <DialogTitle>{file.name}</DialogTitle>
                                                                    </DialogHeader>
                                                                    <div className="mt-2">
                                                                        <img
                                                                            src={URL.createObjectURL(file)}
                                                                            alt={file.name}
                                                                            className="w-full h-auto rounded-lg"
                                                                        />
                                                                    </div>
                                                                </DialogContent>
                                                            </Dialog>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-5 w-5 p-0 rounded-full hover:bg-red-100"
                                                                onClick={() =>
                                                                    setFiles(files.filter((_, i) => i !== index))
                                                                }
                                                            >
                                                                <X className="h-3 w-3 text-red-500" />
                                                            </Button>
                                                        </div>
                                                    </motion.li>
                                                ))}
                                            </ul>
                                        )}
                                    </ScrollArea>
                                </div>
                            </div>
                            <div>
                                <CardTitle className="text-lg font-semibold text-purple-700 my-4">
                                    {agent('ask_a_question')}
                                </CardTitle>
                                <Textarea
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    placeholder={agent('question_placeholder')}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            analyzeImages();
                                        }
                                    }}
                                    className="w-full border-2 border-purple-300 focus:border-purple-500 rounded-xl py-2 px-4 resize-none"
                                    rows={3}
                                />
                            </div>
                            <ButtonComponent
                                loading={loading}
                                disabled={loading}
                                onClick={analyzeImages}
                                label={
                                    <>
                                        <FileSearch2 size={20} className="mr-2" /> {images('analyze_images')}
                                    </>
                                }
                                className="mt-5 primary-gradient-button text-white font-semibold py-2 px-4 rounded-full"
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Permission>
    );
};

export default AnalizeImagesComponent;
